/**
 * Shared colour derivation utilities.
 * Used by AppearancePage (save) and CreateSite (initial setup)
 * to ensure advanced colours are always stored in designVariables.
 */

function hexToHSL(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function adjustHSL(hex: string, hShift: number, sShift: number, lShift: number): string {
  if (!hex || !hex.startsWith('#') || hex.length < 7) return hex || '#888888';
  const [h, s, l] = hexToHSL(hex);
  return hslToHex(
    (h + hShift + 360) % 360,
    Math.max(0, Math.min(100, s + sShift)),
    Math.max(0, Math.min(100, l + lShift))
  );
}

function mixHex(hex: string, amount: number): string {
  if (!hex || !hex.startsWith('#') || hex.length < 7) return hex || '#888888';
  const [h, s, l] = hexToHSL(hex);
  return hslToHex(h, Math.max(0, s + (amount < 0 ? 5 : -10)), Math.max(0, Math.min(100, l + amount)));
}

export function deriveColor(rule: string, primary: string, bg: string, text: string): string {
  switch (rule) {
    case 'lightenPrimary': return adjustHSL(primary, 0, -10, 15);
    case 'shiftPrimary': return adjustHSL(primary, 30, -5, 0);
    case 'accent': return adjustHSL(primary, -30, 0, 5);
    case 'darkenBg': return mixHex(bg, -4);
    case 'surface': return mixHex(bg, 3);
    case 'muteText': return adjustHSL(text, 0, -15, 25);
    case 'heading': return primary;
    case 'border': return adjustHSL(text, 0, -30, 55);
    case 'footerBg': return adjustHSL(primary, 0, -20, -30);
    default: return '#888888';
  }
}

/** The advanced colour fields and their derivation rules */
export const ADVANCED_COLOR_DERIVE_FIELDS = [
  { key: 'colorPrimaryLight', derive: 'lightenPrimary' },
  { key: 'colorAccent', derive: 'accent' },
  { key: 'colorBackgroundAlt', derive: 'darkenBg' },
  { key: 'colorSurface', derive: 'surface' },
  { key: 'colorTextMuted', derive: 'muteText' },
  { key: 'colorBorder', derive: 'border' },
  { key: 'colorFooterBg', derive: 'footerBg' },
] as const;

/**
 * Given a designVariables object with at least the main colours,
 * derive and fill in any missing advanced colours.
 * Does NOT overwrite explicitly-set values.
 */
export function deriveAdvancedColors(vars: Record<string, unknown>): Record<string, unknown> {
  const primary = (vars.colorPrimary as string) || '#3b82f6';
  const bg = (vars.colorBackground as string) || '#ffffff';
  const text = (vars.colorText as string) || '#1a1a1a';
  const result = { ...vars };
  for (const { key, derive } of ADVANCED_COLOR_DERIVE_FIELDS) {
    if (!result[key]) {
      result[key] = deriveColor(derive, primary, bg, text);
    }
  }
  // Highlight (colorSecondary) is a main colour but still needs a
  // fallback derivation when the user hasn't explicitly picked one
  if (!result.colorSecondary) {
    result.colorSecondary = deriveColor('shiftPrimary', primary, bg, text);
  }
  return result;
}
